import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Card,
  CardContent,
  Tabs,
  Tab,
  Box,
  TextField,
  Button,
  Typography,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Stack
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory } from 'react-router-dom';
import PasswordStrength from '../../components/auth/PasswordStrength';
import PasswordField from '../../components/common/PasswordField';
import userService from '../../services/userService';
import { updateUser } from '../../redux/slices/authSlice';
import { validators } from '../../utils/validators';
import alertify from 'alertifyjs';

/**
 * ProfilePage - User profile management with tabs
 * 
 * Features:
 * - Tab 1: View/Edit profile info (firstName, lastName)
 * - Tab 2: Change password
 * 
 * Note: Company info moved to /company/settings
 */
const ProfilePage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Profile edit state
  const [isEditMode, setIsEditMode] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || ''
  });
  const [profileErrors, setProfileErrors] = useState({});
  
  // Password change state
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  
  // User type badge color (Phase 4 refactoring)
  const getUserTypeBadgeColor = (userType) => {
    switch (userType) {
      case 'company_owner': return 'primary';
      case 'company_member': return 'secondary';
      case 'individual': return 'success';
      case 'system_admin': return 'error';
      default: return 'default';
    }
  };
  
  // User type badge label (Phase 4 refactoring)
  const getUserTypeLabel = (userType) => {
    switch (userType) {
      case 'company_owner': return 'Şirket Sahibi';
      case 'company_member': return 'Şirket Üyesi';
      case 'individual': return 'Bireysel Kullanıcı';
      case 'system_admin': return 'Sistem Yöneticisi';
      default: return userType;
    }
  };
  
  // ============================================================================
  // TAB 1: PROFILE INFO HANDLERS
  // ============================================================================
  
  const handleProfileEdit = () => {
    setIsEditMode(true);
    setProfileErrors({});
  };
  
  const handleProfileCancel = () => {
    setIsEditMode(false);
    setProfileForm({
      firstName: user.firstName,
      lastName: user.lastName
    });
    setProfileErrors({});
  };
  
  const validateProfile = () => {
    const errors = {};
    
    const firstNameError = validators.required(profileForm.firstName, 'Ad');
    if (firstNameError) errors.firstName = firstNameError;
    
    const lastNameError = validators.required(profileForm.lastName, 'Soyad');
    if (lastNameError) errors.lastName = lastNameError;
    
    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleProfileSave = async () => {
    if (!validateProfile()) {
      alertify.error('Lütfen tüm alanları doldurun');
      return;
    }
    
    setProfileLoading(true);
    try {
      const { user: updatedUser } = await userService.updateProfile(profileForm);
      
      // Update Redux state
      dispatch(updateUser(updatedUser));
      
      alertify.success('Profil bilgileriniz güncellendi');
      setIsEditMode(false);
    } catch (error) {
      alertify.error(error.response?.data?.message || 'Profil güncellenemedi');
    } finally {
      setProfileLoading(false);
    }
  };
  
  // ============================================================================
  // TAB 2: PASSWORD CHANGE HANDLERS
  // ============================================================================
  
  const validatePassword = () => {
    const errors = {};
    
    if (!passwordForm.currentPassword) {
      errors.currentPassword = 'Mevcut şifre gereklidir';
    }
    
    const newPasswordError = validators.password(passwordForm.newPassword);
    if (newPasswordError) errors.newPassword = newPasswordError;
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'Şifreler eşleşmiyor';
    }
    
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      errors.newPassword = 'Yeni şifre mevcut şifre ile aynı olamaz';
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) {
      alertify.error('Lütfen tüm alanları doğru şekilde doldurun');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await userService.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      
      alertify.success('Şifreniz başarıyla değiştirildi');
      
      // Reset form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordErrors({});
    } catch (error) {
      alertify.error(error.response?.data?.message || 'Şifre değiştirilemedi');
    } finally {
      setPasswordLoading(false);
    }
  };
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  if (!user) {
    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Alert severity="error">Kullanıcı bilgileri yüklenemedi</Alert>
        </Container>
    );
  }
  
  return (
      <Container maxWidth="xl">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
          sx={{ mb: 3 }}
        >
          Geri Dön
        </Button>

        {/* Header */}
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Profil Ayarları
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Hesap bilgilerinizi yönetin
          </Typography>
        </Box>
        
        {/* Card with Tabs */}
        <Card>
          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab icon={<PersonIcon />} iconPosition="start" label="Profil Bilgileri" />
            <Tab icon={<LockIcon />} iconPosition="start" label="Şifre Değiştir" />
          </Tabs>
          
          <CardContent sx={{ p: 4 }}>
        
            {/* Tab 1: Profile Info */}
            {activeTab === 0 && (
              <Box>
                {!isEditMode ? (
              // VIEW MODE
              <Stack spacing={3} sx={{ maxWidth: 800 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <Box sx={{ flex: '1 1 250px' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      Ad
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {user.firstName}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ flex: '1 1 250px' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      Soyad
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {user.lastName}
                    </Typography>
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {user.email}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Hesap Tipi
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={getUserTypeLabel(user.userType)}
                      color={getUserTypeBadgeColor(user.userType)}
                    />
                  </Box>
                </Box>
                
                {/* Şirket Adı - Sadece company_owner ve company_member için */}
                {(user.userType === 'company_owner' || user.userType === 'company_member') && user.companyId && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      Şirket
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {typeof user.companyId === 'object' 
                        ? (user.companyId.companyName || user.companyId.name || 'Şirket adı bulunamadı')
                        : 'Şirket bilgisi yükleniyor...'}
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ pt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleProfileEdit}
                  >
                    Düzenle
                  </Button>
                </Box>
              </Stack>
            ) : (
              // EDIT MODE
              <Stack spacing={3} sx={{ maxWidth: 800 }}>
                <Alert severity="info" icon={<PersonIcon />}>
                  Profil bilgilerinizi güncelleyebilirsiniz. Email adresi değiştirilemez.
                </Alert>

                <TextField
                  fullWidth
                  label="Ad *"
                  value={profileForm.firstName}
                  onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
                  error={!!profileErrors.firstName}
                  helperText={profileErrors.firstName}
                  disabled={profileLoading}
                  autoFocus
                />
                
                <TextField
                  fullWidth
                  label="Soyad *"
                  value={profileForm.lastName}
                  onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
                  error={!!profileErrors.lastName}
                  helperText={profileErrors.lastName}
                  disabled={profileLoading}
                />
                
                <Box sx={{ display: 'flex', gap: 2, pt: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={profileLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handleProfileSave}
                    disabled={profileLoading}
                    size="large"
                  >
                    {profileLoading ? 'Kaydediliyor...' : 'Kaydet'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleProfileCancel}
                    disabled={profileLoading}
                    size="large"
                  >
                    İptal
                  </Button>
                </Box>
                </Stack>
              )}
            </Box>
            )}
            
            {/* Tab 2: Password Change */}
            {activeTab === 1 && (
              <Box>
            <form onSubmit={handlePasswordChange}>
              <Stack spacing={3} sx={{ maxWidth: 800 }}>
                <Alert severity="info" icon={<LockIcon />}>
                  Güvenliğiniz için güçlü bir şifre seçin. Şifreniz en az 8 karakter, 1 büyük harf ve 1 rakam içermelidir.
                </Alert>
                
                <PasswordField
                  label="Mevcut Şifre"
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  error={!!passwordErrors.currentPassword}
                  helperText={passwordErrors.currentPassword}
                  disabled={passwordLoading}
                  autoComplete="current-password"
                />
                
                <Divider />
                
                <PasswordField
                  label="Yeni Şifre"
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  error={!!passwordErrors.newPassword}
                  helperText={passwordErrors.newPassword}
                  disabled={passwordLoading}
                  autoComplete="new-password"
                />
                
                {/* Password Strength Indicator */}
                <PasswordStrength password={passwordForm.newPassword} />
                
                <PasswordField
                  label="Yeni Şifre (Tekrar)"
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  error={!!passwordErrors.confirmPassword}
                  helperText={passwordErrors.confirmPassword}
                  disabled={passwordLoading}
                  autoComplete="new-password"
                />
                
                <Box sx={{ pt: 3 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={passwordLoading ? <CircularProgress size={20} color="inherit" /> : <LockIcon />}
                    disabled={passwordLoading}
                    size="large"
                  >
                    {passwordLoading ? 'Değiştiriliyor...' : 'Şifreyi Değiştir'}
                  </Button>
                </Box>
              </Stack>
              </form>
            </Box>
            )}
          </CardContent>
        </Card>
      </Container>
  );
};

export default ProfilePage;

